import axios from "axios";
import saveAs from "file-saver";
import api from "../api/api";
import { apiUrl } from "../api/config";
import IBoeModel from "../interfaces/IBoeModel";
import IPasswordResetResponse from "../interfaces/IPasswordResetResponse";
import IUser, { ICompanyColorSchemeSettings } from "../interfaces/IUser";
import IUserActivationRequest from "../interfaces/IUserActivationRequest";
import IUserActivationResponse from "../interfaces/IUserActivationResponse";
import IUserProfileSettingsRequest from "../interfaces/IUserProfileSettingsRequest";
import IUserSettings from "../interfaces/IUserSettings";

export default class IdentityService {
    async getIdentity(email: string): Promise<IUser> {
        const response = await api.get<IUserResponse>("/user/profile");

        return {
            firstName: response.data.firstName,
            lastName: response.data.lastName,
            email: email,
            isIndividual: response.data.isIndividual,
            logoUrl: response.data.logoUrl,
            enableDataExports: response.data.enableDataExports,
            gaUserId: response.data.gaUserId,
            companyColorSchemeSettings: response.data.companyColorSchemeSettings,
        };
    }

    async getUserSettings(): Promise<IUserSettings> {
        const response = await api.get<IUserSettingsResponse>("/user/settings");

        return {
            profileSettings: {
                companyName: response.data.companyName,
                title: response.data.title,
            },
            preferencesSettings: {
                sendEmailsWithImportantUpdates: response.data.sendEmailsWithImportantUpdates,
                filterProjectUpdatesByFavorites: response.data.filterProjectUpdatesByFavorites,
            },
            mapboxStyleUrl: response.data.mapboxStyleUrl,
            publicRegridAccessToken: response.data.publicRegridAccessToken,
            publicMapboxAccessToken: response.data.publicMapboxAccessToken,
            publicGoogleAccessToken: response.data.publicGoogleAccessToken,
        };
    }

    async downloadBoe(model: IBoeModel): Promise<void> {
        const res = await api.post("/user/downloadboe", model, { responseType: "blob" });

        const contentDisposition = (res.headers["content-disposition"] as string) || "";
        const filename = (contentDisposition.split("filename=")[1]?.split(";")[0] || "BOE.xlsx")
            .replace(/^["']|["']$/g, "");

        saveAs(res.data, filename);
    }

    async appLoad() {
        await api.post("/user/appload");
    }

    async updateUserProfileSettings(request: IUserProfileSettingsRequest): Promise<void> {
        await api.post("/user/profileSettings", request);
    }

    async requestPasswordReset(email: string): Promise<IPasswordResetResponse> {
        const response = await axios.post(apiUrl + "/user/resetpassword", {
            email: email,
        });

        return response.data;
    }

    async setNewPassword(email: string, password: string, resetToken: string): Promise<void> {
        await axios.post(apiUrl + "/user/setpassword", {
            email: email,
            password: password,
            resetToken: resetToken,
        });
    }

    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
        await api.post("/user/changepassword", {
            currentPassword: currentPassword,
            newPassword: newPassword,
        });
    }

    async requestActivationLink(email: string): Promise<void> {
        await axios.post(apiUrl + "/user/resendactivationemail", {
            email: email,
        });
    }

    async getActivationData(token: string): Promise<IUserActivationResponse> {
        const response = await axios.get(apiUrl + "/user/activation/" + token);
        return response.data
    }

    async sendActivationData(request: IUserActivationRequest): Promise<void> {
        await axios.post(apiUrl + "/user/activation", request);
    }
}

interface IUserResponse {
    firstName: string;
    lastName: string;
    isIndividual: boolean;
    isTrial: boolean;
    enableDataExports: boolean;
    logoUrl: string;
    gaUserId: string;
    companyColorSchemeSettings: ICompanyColorSchemeSettings
}

interface IUserSettingsResponse {
    companyName: string;
    title: string;
    sendEmailsWithImportantUpdates: boolean;
    filterProjectUpdatesByFavorites: boolean;
    mapboxStyleUrl: string;
    publicRegridAccessToken: string;
    publicMapboxAccessToken: string;
    publicGoogleAccessToken: string;
}